import { Datatype } from '../../../../../expressions/datatype/Datatype.ts';
import {
  type IReference,
  ReferenceType,
} from '../../../../../expressions/references/IReference.ts';
import { type IReferencePath } from '../../../../../expressions/references/IReferencePath.ts';
import { VirtualReferenceType } from '../../../../../expressions/references/virtual/VirtualReferenceType.ts';
import { WidgetRepository } from '../../../WidgetRepository.ts';
import { type ITableInputWidget } from '../ITableInputWidget.ts';
import { type ITableRow } from '../ITableRow.ts';

export function encodeRowAsVirtualTypeValue(row: ITableRow) {
  return row.code ?? row.id;
}

export const VirtualRowCodeReferenceSuffix = 'virtualRowCode@internal';

/**
 * Per le tabelle di input, dobbiamo aggiungere come Riferimenti disponibili
 * le colonne, che sono "virtuali", in quanto non stanno nei `children`
 * del Widget (dopo la materializzazione).
 *
 * Es. colonna "nome" -> diventa "it_nome", "fr_nome" in base alle righe
 *
 * Permettiamo quindi, tramite questa funzione, di aggiungere le reference
 * a "nome" che a runtime diventeranno "it_nome", "fr_nome" ecc. in base
 * alla riga.
 */
export function getTableInputVirtualColumnsReferences(
  path: IReferencePath,
  tableWidget: ITableInputWidget,
): IReference[] {
  const references: IReference[] = [];

  /**
   * Aggiungo la reference al codice della riga
   */
  references.push({
    type: ReferenceType.VirtualColumn,
    virtualType: VirtualReferenceType.RowCode,
    path,
    code: `${VirtualRowCodeReferenceSuffix}`,
    id: `${tableWidget.id}@virtualRowCode`,
    label: `${tableWidget.code}: Codice Riga`,
    field: {
      type: Datatype.String,
      options: tableWidget.rows.map(row => {
        const value = encodeRowAsVirtualTypeValue(row);
        return {
          value,
          label: value,
        };
      }),
    },
  });

  for (const columnWidget of tableWidget.columns) {
    const columnType = WidgetRepository.find(columnWidget.type);

    // Prendiamo la Reference del widget colonna come se fosse "reale"
    const columnRef = columnType?.getReference?.(path, columnWidget);

    // Se la colonna ha una reference, la aggiungiamo alle reference disponibili
    if (!columnRef) continue;

    const columnSiblingRefs =
      columnType?.getSiblingReferences?.(path, columnWidget) ?? [];

    references.push(
      ...[columnRef, ...columnSiblingRefs].map(ref => ({
        ...ref,
        type: ReferenceType.VirtualColumn,
      })),
    );
  }

  return references;
}
